import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Alert,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Security,
  Save,
  Edit,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);



  const handleSaveSettings = () => {
    // Save settings to backend/local storage
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('settings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('settings.subtitle')}
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
          {t('settings.saveSuccess')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Profile Settings */}
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Security sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('settings.profileSecurity')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('settings.email')}
                    secondary={user?.email || t('settings.notSet')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ListItemSecondaryAction>
                    <Chip label={t('settings.verified')} color="success" size="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('settings.password')}
                    secondary={t('settings.lastChanged')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => setProfileDialogOpen(true)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('settings.twoFactor')}
                    secondary={t('settings.notEnabled')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ListItemSecondaryAction>
                    <Button size="small" variant="outlined">
                      {t('settings.enable')}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>







        {/* Save Button */}
        <Box sx={{ flex: '1 1 100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              {t('settings.reset')}
            </Button>
            <Button
              variant="contained"
              sx={{ gap: 1 }}
              onClick={handleSaveSettings}
            >
              <Save sx={{ fontSize: 18 }} />{t('settings.saveChanges')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.changePassword')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label={t('settings.currentPassword')}
              type="password"
              size="small"
              autoComplete="current-password"
            />
            <TextField
              fullWidth
              label={t('settings.newPassword')}
              type="password"
              size="small"
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              label={t('settings.confirmPassword')}
              type="password"
              size="small"
              autoComplete="new-password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>{t('settings.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setProfileDialogOpen(false);
              setSaveSuccess(true);
            }}
          >
            {t('settings.updatePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
